import { Link } from 'react-router-dom';
import { PageHeader } from '@/components/PageHeader';
import { StatusBadge } from '@/components/StatusBadge';
import { useDashboardSummary } from '@/hooks/useDesk';
import { brand } from '@/lib/brand';
import { formatDate } from '@/lib/format';
import { formatEventPlace } from '@/types/desk';

export function DashboardPage() {
  const { data, isLoading, isError, error } = useDashboardSummary();

  return (
    <div>
      <PageHeader
        title="Dashboard"
        description={`${brand.org} venue events desk — enquiry → event → quote → invoice.`}
      />

      {isLoading ? (
        <p className="animate-pulse-soft text-sm text-[var(--muted)]">
          Loading desk summary…
        </p>
      ) : null}
      {isError ? (
        <p className="text-sm text-dome-danger" role="alert">
          {(error as Error).message}
        </p>
      ) : null}

      {data ? (
        <>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
            {(
              [
                ['Open enquiries', data.counts.openEnquiries, '/enquiries'],
                ['Active events', data.counts.activeEvents, '/events'],
                ['Open quotes', data.counts.openQuotes, '/quotes'],
                ['Open invoices', data.counts.openInvoices, '/invoices'],
                ['Clients', data.counts.clients, '/clients'],
              ] as const
            ).map(([label, count, to]) => (
              <Link
                key={to}
                to={to}
                className="rounded-xl border border-[var(--border)] bg-white/80 px-4 py-3 transition hover:border-dome-leaf/40"
              >
                <p className="text-xs text-[var(--muted)]">{label}</p>
                <p className="font-display text-2xl text-dome-forest">{count}</p>
              </Link>
            ))}
          </div>

          <div className="mt-6 grid gap-4 lg:grid-cols-2">
            <section className="rounded-xl border border-[var(--border)] bg-white/80 p-4">
              <div className="mb-3 flex items-center justify-between">
                <h2 className="font-display text-lg text-dome-forest">
                  Recent enquiries
                </h2>
                <Link to="/enquiries" className="text-sm text-dome-leaf">
                  View all
                </Link>
              </div>
              <ul className="space-y-2 text-sm">
                {data.recentEnquiries.length === 0 ? (
                  <li className="text-[var(--muted)]">No enquiries yet.</li>
                ) : (
                  data.recentEnquiries.map((e) => (
                    <li
                      key={e.id}
                      className="flex items-center justify-between gap-2"
                    >
                      <span className="truncate">
                        {e.event_name || e.contact_name}
                      </span>
                      <StatusBadge status={e.status} />
                    </li>
                  ))
                )}
              </ul>
            </section>

            <section className="rounded-xl border border-[var(--border)] bg-white/80 p-4">
              <div className="mb-3 flex items-center justify-between">
                <h2 className="font-display text-lg text-dome-forest">
                  Upcoming events
                </h2>
                <Link to="/events" className="text-sm text-dome-leaf">
                  View all
                </Link>
              </div>
              <ul className="space-y-2 text-sm">
                {data.upcomingEvents.length === 0 ? (
                  <li className="text-[var(--muted)]">No upcoming events.</li>
                ) : (
                  data.upcomingEvents.map((e) => (
                    <li
                      key={e.id}
                      className="flex items-center justify-between gap-2"
                    >
                      <span className="min-w-0 truncate">
                        <Link
                          to={`/events/${e.id}`}
                          className="font-medium text-dome-forest underline-offset-2 hover:underline"
                        >
                          {e.name}
                        </Link>
                        <span className="ml-2 text-xs text-[var(--muted)]">
                          {formatDate(e.starts_at)} · {formatEventPlace(e)}
                        </span>
                      </span>
                      <StatusBadge status={e.status} />
                    </li>
                  ))
                )}
              </ul>
            </section>
          </div>

          <div className="mt-6 flex flex-wrap gap-3">
            <Link
              to="/quotes/quick"
              className="rounded-lg bg-dome-brass px-4 py-2 text-sm font-medium text-dome-ink"
            >
              Quick quote
            </Link>
            <Link
              to="/enquiries"
              className="rounded-lg bg-dome-forest px-4 py-2 text-sm font-medium text-dome-sand"
            >
              New enquiry
            </Link>
          </div>
        </>
      ) : null}
    </div>
  );
}
